import { createAsyncThunk } from "@reduxjs/toolkit";
import { API, Auth } from "aws-amplify";
import axios from "axios";
import { wait } from "src/utils/wait";
import { signedLambdaFetch } from "src/utils/imageGeneration";
import { snackAdded } from "../system";

import * as Sentry from "@sentry/nextjs";


// Meta title/description and AI keywords run on IAM-authed Lambda Function URLs
// (one per Lambda), so these calls are SigV4-signed with the signed-in user's
// credentials, like brand guidelines and image generation. The public REST APIs
// they replaced had no auth.
const metaTitleUrl = process.env.NEXT_PUBLIC_META_TITLE_LAMBDA_URL
const metaDescriptionUrl = process.env.NEXT_PUBLIC_META_DESCRIPTION_LAMBDA_URL
const aiKeywordsUrl = process.env.NEXT_PUBLIC_AI_KEYWORDS_LAMBDA_URL

// A missing URL is a build/config error: fail the call (Sentry sees it) rather
// than send anything unsigned.
const requireUrl = (url: string | undefined, name: string): string => {
  if (!url) throw new Error(`${name} is not set`);
  return url;
}
const getInstructionsLambdaUrl = process.env.NEXT_PUBLIC_BRIEF_INSTRUCTIONS_LAMBDA_URL
const scraperUrl = process.env.NEXT_PUBLIC_SCRAPER_LAMBDA_URL
const instructionsFunctionUrl = "https://tzq4phrlzc7ouo6emua3qwua7m0qqfgv.lambda-url.eu-central-1.on.aws/"


export const getTitleSuggestions = createAsyncThunk(
  "briefs/getTitleSuggestions",
  async ({
    keyword,
    competitors,
    briefId,
    regenerate = false,
    language,
    requestType,
  }: {
    keyword: string;
    competitors: string[] | number[];
    briefId: string;
    regenerate?: boolean;
    language: string;
    requestType: string;
  }, { dispatch }) => {
    const request = async (retry: number) => {
      if (retry > 3) {
        throw new Error("retry limit reached");
      }
      try {
        const { data } = await signedLambdaFetch(
          requireUrl(metaTitleUrl, "NEXT_PUBLIC_META_TITLE_LAMBDA_URL"),
          {
            keyword,
            titles: competitors,
            language,
            requestType,
          }
        );
        if (data && "titles" in data) {
          return (data.titles);
        }
        else {
          return ([]);
        }
        // return data;
      } catch (err) {
        Sentry.captureException(err);
        dispatch(snackAdded({ message: "Could not get title suggestions. Please try again.", type: "error" }));
        throw err;
      }
    };

    const result = await request(1);
    return result;
  }
);

export const getHeadingsSuggestions = createAsyncThunk(
  "suggestions/getHeadingsSuggestions",
  async ({
    keyword,
    regenerate = false,
    briefId,
    maxHeadings,
    startAt,
    title
  }: {
    keyword: string;
    briefId: string;
    maxHeadings: number;
    regenerate?: boolean;
    startAt: string;
    title: string;

  }) => {
    const request = async (retry: number, outlineRetry: boolean = false) => {
      if (retry > 10) {
        throw new Error("retry limit reached");
      }
      try {
        const { data } = await API.get(
          "suggestions",
          `/suggestions/headings?briefId=${briefId}&maxHeadings=${maxHeadings}&regenerate=${retry === 1 ? regenerate : "false"
          }&outlineRetry=${outlineRetry}&title=${title}`,
          {}
        );
        if (data == true) {
          await wait(2000);
          return await request(retry + 1, true);
        }
        return data;
      } catch (err) {
        Sentry.captureException(err);
        console.log("Error in getHeadingsSuggestions:", err);

        // Add safety check for err.response
        if (err && err.response && err.response.status) {
          if (Number(err.response.status) === 504) {
            await wait(2000);
            return await request(retry + 1);
          } else if (Number(err.response.status) === 505) {
            await wait(2000);
            return await request(retry + 1, true);
          }
        }

        // Check if it's a network error - network errors often don't have response object
        if (err && err.message && err.message.includes("Network Error")) {
          console.log("Network error detected, retrying...");
          await wait(2000);
          return await request(retry + 1);
        }

        // Return a graceful error object if we can't retry
        return { error: true, message: err?.message || "An error occurred" };
      }
    };

    const result = await request(1);
    return result;
  }
);

export const statisticsRequested = createAsyncThunk(
  "briefs/statisticsRequested",
  async (keyword: string) => {
    try {
      return [];
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getDescriptionSuggestions = createAsyncThunk(
  "briefs/getDescriptionSuggestions",
  async ({
    keyword,
    competitors,
    title,
    briefId,
    regenerate = false,
    language,
    requestType
  }: {
    keyword: string;
    competitors: string[] | number[];
    title: string;
    briefId: string;
    regenerate?: boolean;
    language: string;
    requestType: string;
  }, { dispatch }) => {
    const request = async (retry: number) => {
      if (retry > 3) {
        throw new Error("retry limit reached");
      }
      try {
        const { data } = await signedLambdaFetch(
          requireUrl(metaDescriptionUrl, "NEXT_PUBLIC_META_DESCRIPTION_LAMBDA_URL"),
          {
            keyword,
            title,
            descriptions: competitors,
            language,
            requestType,
          }
        );
        if (data && "descriptions" in data) {
          return (data.descriptions);
        }
        else {
          return ([]);
        }
      } catch (err) {
        Sentry.captureException(err);
        dispatch(snackAdded({ message: "Could not get description suggestions. Please try again.", type: "error" }));
        throw err;
      }
    };
    const result = await request(1);
    return result;
  }
);

export const getAITopics = createAsyncThunk(
  "briefs/getAITopics",
  async ({
    id,
    selectedOutlines,
    title,
  }:
    {
      id: string,
      selectedOutlines: any[],
      title: string,
    }, { dispatch }) => {
    const request = async (retry: number) => {
      if (retry > 3) {
        throw new Error("retry limit reached");
      }
      try {
        const { data } = await signedLambdaFetch(
          requireUrl(aiKeywordsUrl, "NEXT_PUBLIC_AI_KEYWORDS_LAMBDA_URL"),
          {
            briefId: id,
            selectedOutlines,
            title,
          }
        );
        return data;
      } catch (err) {
        Sentry.captureException(err);
        dispatch(snackAdded({ message: "Could not get AI topics. Please try again.", type: "error" }));
        throw err;
      }
    }
    const result = await request(1);
    return result;

  }


);

export const generateProjectLevelInstructions = createAsyncThunk(
  "suggestions/generateProjectLevelInstructions",
  async ({
    projectId,
  }: {
    projectId: string;
  }) => {
    const request = async (retry: number) => {
      if (retry > 3) {
        throw new Error("retry limit reached - project instructions generation failed");
      }
      try {
        // Check if we should use IAM authentication
        if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
          try {
            // Import AWS SDK modules for signing
            const { SignatureV4 } = require('@aws-sdk/signature-v4');
            const { Sha256 } = require('@aws-crypto/sha256-browser');
            const { HttpRequest } = require('@aws-sdk/protocol-http');

            // Parse the Lambda Function URL
            const lambdaUrl = new URL(instructionsFunctionUrl);

            // Get temporary credentials from Cognito Identity Pool
            const credentials = await Auth.currentCredentials();

            // Create request body
            const requestBody = {
              projectId,
              mode: "project" // Indicate this is project-level generation
            };

            // Create request object for signing (includes host)
            const request = new HttpRequest({
              method: 'POST',
              hostname: lambdaUrl.hostname,
              path: lambdaUrl.pathname,
              headers: {
                'Content-Type': 'application/json',
                'host': lambdaUrl.hostname, // Required for signing
              },
              body: JSON.stringify(requestBody),
            });

            // Create signer
            const signer = new SignatureV4({
              credentials,
              region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
              service: 'lambda',
              sha256: Sha256,
            });

            // Sign the request
            const signedRequest = await signer.sign(request);

            // Remove host header from signed headers for browser compatibility
            const headersForRequest = { ...signedRequest.headers };
            delete headersForRequest.host;

            console.log("Making IAM-authenticated request to instructions Lambda Function URL");

            // Make the request with the signed headers (excluding host)
            const response = await axios({
              url: instructionsFunctionUrl,
              method: 'POST',
              headers: headersForRequest,
              data: JSON.stringify(requestBody),
              validateStatus: function (status) {
                // Don't throw on 403, we want to inspect the response
                return status < 500;
              }
            });

            if (response.status === 403) {
              console.error("403 Response Headers:", response.headers);
              console.error("403 Response Data:", response.data);
              throw new Error(`Lambda Function URL returned 403: ${JSON.stringify(response.data)}`);
            }

            const data = response.data;

            if (!data.success) {
              throw new Error(data.error || "Failed to generate project instructions");
            }

            // Return the instructions text
            if (typeof data.instructions === 'object' && data.instructions.article_instructions) {
              return data.instructions.article_instructions;
            }
            return data.instructions || "";

          } catch (signingError) {
            console.error("Error signing request:", signingError);
            console.error("Lambda URL:", instructionsFunctionUrl);
            console.error("Auth type:", process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE);
            console.error("AWS Region:", process.env.NEXT_PUBLIC_AWS_REGION);

            // Get current user info to identify which role is being used
            try {
              const user = await Auth.currentAuthenticatedUser();
              console.error("Current user:", user?.username);
              console.error("User attributes:", user?.attributes);
            } catch (userError) {
              console.error("Could not get current user:", userError);
            }

            // Extract function URL ID for better debugging
            const functionUrlId = new URL(instructionsFunctionUrl).hostname.split('.')[0];
            console.error("ERROR: Your Cognito Identity Pool authenticated role needs permission to invoke Lambda Function URLs.");
            console.error("Required IAM permissions:");
            console.error("1. lambda:InvokeFunctionUrl");
            console.error("2. Possibly lambda:InvokeFunction");
            console.error("Function URL ID:", functionUrlId);
            console.error("Expected IAM role: amplify-seowind-staging-73051-authRole");
            console.error("Add this policy to your Cognito authenticated role:");
            console.error(`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "lambda:InvokeFunctionUrl",
        "lambda:InvokeFunction"
      ],
      "Resource": "*"
    }
  ]
}`);
            throw signingError;
          }
        } else {
          // If not using AWS_IAM auth, make a regular request
          const response = await axios.post(instructionsFunctionUrl, {
            projectId,
            mode: "project"
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const data = response.data;

          if (!data.success) {
            throw new Error(data.error || "Failed to generate project instructions");
          }

          // Return the instructions text
          if (typeof data.instructions === 'object' && data.instructions.article_instructions) {
            return data.instructions.article_instructions;
          }
          return data.instructions || "";
        }
      } catch (error) {
        console.error(`Attempt ${retry} failed:`, error);
        if (retry >= 3) {
          throw error;
        }
        await wait(1000 * retry); // Exponential backoff
        return request(retry + 1);
      }
    };
    const result = await request(1);
    return result;
  }
);

export const generateAutomatedInstructions = createAsyncThunk(
  "suggestions/generateAutomatedInstructions",
  async ({
    projectId,
    briefId,
  }: {
    projectId: string;
    briefId: string;
  }, { rejectWithValue }) => {
    try {
      if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
        try {
          const { SignatureV4 } = require('@aws-sdk/signature-v4');
          const { Sha256 } = require('@aws-crypto/sha256-browser');
          const { HttpRequest } = require('@aws-sdk/protocol-http');

          const lambdaUrl = new URL(getInstructionsLambdaUrl);
          const credentials = await Auth.currentCredentials();

          const request = new HttpRequest({
            method: 'POST',
            hostname: lambdaUrl.hostname,
            path: lambdaUrl.pathname,
            headers: {
              'Content-Type': 'application/json',
              'host': lambdaUrl.hostname,
            },
            body: JSON.stringify({ projectId, briefId }),
          });

          const signer = new SignatureV4({
            credentials,
            region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
            service: 'lambda',
            sha256: Sha256,
          });

          const signedRequest = await signer.sign(request);
          const { host, ...headersForRequest } = signedRequest.headers;

          const response = await axios({
            url: getInstructionsLambdaUrl,
            method: 'POST',
            headers: headersForRequest,
            data: JSON.stringify({ projectId, briefId }),
          });

          if (!response.data.success) {
            throw new Error(response.data.error || "Failed to generate instructions");
          }

          return response.data.instructions;
        } catch (signingError) {
          console.error("Error signing instructions request:", signingError);
          Sentry.captureException(signingError);
          return rejectWithValue({ success: false, error: "Failed to authenticate with AWS" });
        }
      } else {
        const retryRequest = async (retry: number): Promise<string> => {
          if (retry > 3) {
            throw new Error("retry limit reached - instructions generation failed");
          }
          try {
            const response = await fetch(getInstructionsLambdaUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ projectId, briefId }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
              throw new Error(data.error || "Failed to generate instructions");
            }

            return data.instructions;
          } catch (err) {
            console.error(`Instructions generation attempt ${retry} failed:`, err);
            Sentry.captureException(err);

            if (retry < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000 * retry));
              return retryRequest(retry + 1);
            }

            throw err;
          }
        };

        return await retryRequest(1);
      }
    } catch (error) {
      console.error("Error calling instructions lambda:", error);
      Sentry.captureException(error);
      return rejectWithValue({ success: false, error: "Failed to generate instructions" });
    }
  }
);

export const generateAutomatedInstructionsForOptimize = createAsyncThunk(
  "suggestions/generateAutomatedInstructionsForOptimize",
  async ({
    projectId,
    title,
    description,
    outline,
    content,
  }: {
    projectId: string;
    title: string;
    description: string;
    outline: string;
    content?: string;
  }, { rejectWithValue }) => {
    try {
      if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
        try {
          const { SignatureV4 } = require('@aws-sdk/signature-v4');
          const { Sha256 } = require('@aws-crypto/sha256-browser');
          const { HttpRequest } = require('@aws-sdk/protocol-http');

          const lambdaUrl = new URL(getInstructionsLambdaUrl);
          const credentials = await Auth.currentCredentials();

          const request = new HttpRequest({
            method: 'POST',
            hostname: lambdaUrl.hostname,
            path: lambdaUrl.pathname,
            headers: {
              'Content-Type': 'application/json',
              'host': lambdaUrl.hostname,
            },
            body: JSON.stringify({ projectId, title, description, outline, content }),
          });

          const signer = new SignatureV4({
            credentials,
            region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
            service: 'lambda',
            sha256: Sha256,
          });

          const signedRequest = await signer.sign(request);
          const { host, ...headersForRequest } = signedRequest.headers;

          const response = await axios({
            url: getInstructionsLambdaUrl,
            method: 'POST',
            headers: headersForRequest,
            data: JSON.stringify({ projectId, title, description, outline, content }),
          });

          if (!response.data.success) {
            throw new Error(response.data.error || "Failed to generate instructions");
          }

          return response.data.instructions;
        } catch (signingError) {
          console.error("Error signing instructions request:", signingError);
          Sentry.captureException(signingError);
          return rejectWithValue({ success: false, error: "Failed to authenticate with AWS" });
        }
      } else {
        const retryRequest = async (retry: number): Promise<string> => {
          if (retry > 3) {
            throw new Error("retry limit reached - instructions generation failed");
          }
          try {
            const response = await fetch(getInstructionsLambdaUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ projectId, title, description, outline, content }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
              throw new Error(data.error || "Failed to generate instructions");
            }

            return data.instructions;
          } catch (err) {
            console.error(`Instructions generation attempt ${retry} failed:`, err);
            Sentry.captureException(err);

            if (retry < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000 * retry));
              return retryRequest(retry + 1);
            }

            throw err;
          }
        };

        return await retryRequest(1);
      }
    } catch (error) {
      console.error("Error calling instructions lambda:", error);
      Sentry.captureException(error);
      return rejectWithValue({ success: false, error: "Failed to generate instructions" });
    }
  }
);

// Scrape article for optimize content workflow
export const scrapeArticleForOptimize = createAsyncThunk<any, string>(
  "suggestions/scrapeArticleForOptimize",
  async (url: string, { rejectWithValue }) => {
    try {
      // Check if AWS IAM auth is required
      if (process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE === 'AWS_IAM') {
        try {
          // Make sure these packages are imported
          const { SignatureV4 } = require('@aws-sdk/signature-v4');
          const { Sha256 } = require('@aws-crypto/sha256-browser');
          const { HttpRequest } = require('@aws-sdk/protocol-http');

          // Parse the Lambda Function URL
          const lambdaUrl = new URL(scraperUrl);
          // Get temporary credentials from Cognito Identity Pool
          const credentials = await Auth.currentCredentials();

          // Extract potential function name from Function URL
          const functionUrlId = lambdaUrl.hostname.split('.')[0];

          // Create request object
          const request = new HttpRequest({
            method: 'POST',
            hostname: lambdaUrl.hostname,
            path: lambdaUrl.pathname,
            headers: {
              'Content-Type': 'application/json',
              'host': lambdaUrl.hostname, // Required for signing
            },
            body: JSON.stringify({ url, output_format: "markdown", get_metadata: true }),
          });

          // Create signer
          const signer = new SignatureV4({
            credentials,
            region: process.env.NEXT_PUBLIC_AWS_REGION || 'eu-central-1',
            service: 'lambda',
            sha256: Sha256,
          });

          // Sign the request
          const signedRequest = await signer.sign(request);

          // Remove host header from signed headers for browser compatibility
          const { host, ...headersForRequest } = signedRequest.headers;

          // Make the request with the signed headers (excluding host)
          const response = await axios({
            url: scraperUrl,
            method: 'POST',
            headers: headersForRequest,
            data: JSON.stringify({ url, output_format: "markdown", get_metadata: true }),
            validateStatus: function (status) {
              // Don't throw on 403, we want to inspect the response
              return status < 500;
            }
          });

          if (response.status === 403) {
            console.error("403 Response Headers:", response.headers);
            console.error("403 Response Data:", response.data);
            console.error("ERROR: Your Cognito Identity Pool authenticated role needs permission to invoke Lambda Function URLs.");
            console.error("Required IAM permissions:");
            console.error("1. lambda:InvokeFunctionUrl");
            console.error("2. Possibly lambda:InvokeFunction");
            throw new Error(`Lambda Function URL returned 403: ${JSON.stringify(response.data)}`);
          }

          const result = response.data;

          // Transform new format to legacy format for backward compatibility
          if (result.data && Array.isArray(result.data)) {
            result.article_text = result.data[0] || "";
            result.article_title = result.data[1] || "";
            result.article_meta_description = result.data[2] || "";
          }

          return result;
        } catch (signingError) {
          console.error("Error signing request:", signingError);
          console.error("Lambda URL:", scraperUrl);
          console.error("Auth type:", process.env.NEXT_PUBLIC_LAMBDA_AUTH_TYPE);
          console.error("AWS Region:", process.env.NEXT_PUBLIC_AWS_REGION);

          // Get current user info to identify which role is being used
          try {
            const user = await Auth.currentAuthenticatedUser();
            console.error("Current user:", user?.username);
            console.error("User attributes:", user?.attributes);
          } catch (userError) {
            console.error("Could not get current user:", userError);
          }

          Sentry.captureException(signingError);
          return rejectWithValue({
            success: false,
            error: "Failed to authenticate with AWS"
          });
        }
      } else {
        // If not using AWS_IAM auth, make a regular request
        const response = await axios.post(scraperUrl, { url, output_format: "markdown", get_metadata: true }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const result = response.data;

        // Transform new format to legacy format for backward compatibility
        if (result.data && Array.isArray(result.data)) {
          result.article_text = result.data[0] || "";
          result.article_title = result.data[1] || "";
          result.article_meta_description = result.data[2] || "";
        }

        return result;
      }
    } catch (error) {
      console.error("Error calling Lambda scraper function:", error);
      Sentry.captureException(error);
      return rejectWithValue({
        success: false,
        error: "Failed to scrape article"
      });
    }
  }
);
